import type { SageOfficeView } from "../../sage-offices/types";
import type { WorkspaceView } from "../../workspaces/types";
import { csvEscape } from "../../common/csv";
import {
    assetTypes,
    assignmentActions,
    canonicalAssetType,
    canonicalAssignmentAction,
    type BulkAssetRow,
} from "../types";

/**
 * Per-row state in the parser output. Carries:
 *
 *   - `rowIndex`: 1-based source line number, matching the spreadsheet
 *     row the user sees ("row 1" is the header; data starts at row 2).
 *   - `payload`: the row in the same shape we'll POST. The id (and
 *     assignment id, when assigning) is generated *here* (parse time)
 *     and held for the lifetime of the page, so a retry of the same
 *     parsed table replays the same UUIDs and the backend's id-conflict
 *     path becomes "skipped".
 *   - `errors`: per-row validation messages. A non-empty list disables
 *     the import button until the user fixes the source CSV and
 *     re-uploads.
 *   - `sageOfficeName` / `workspaceName`: resolved labels for the
 *     preview table. `null` when the id is unknown so the column can
 *     render the raw id as a fallback.
 */
export interface ParsedAssetRow {
    readonly rowIndex: number;
    readonly payload: BulkAssetRow;
    readonly errors: readonly string[];
    readonly sageOfficeName: string | null;
    readonly workspaceName: string | null;
}

export interface ParseAssetsCsvResult {
    /** Top-level errors that prevented row-level parsing (no header, empty file, etc.). */
    readonly fatalError: string | null;
    readonly rows: readonly ParsedAssetRow[];
    readonly hasErrors: boolean;
}

/**
 * Required columns. `assignmentAction` is required so blank assignment
 * columns are not ambiguous on an existing asset (create-unassigned vs
 * leave-current). `assetId` is optional — serial number is the fallback
 * identity on re-upload.
 */
const REQUIRED_HEADERS = [
    "sageOfficeId",
    "type",
    "make",
    "model",
    "serialNumber",
    "assignmentAction",
] as const;
const ALL_HEADERS = [
    "assetId",
    "sageOfficeId",
    "type",
    "make",
    "model",
    "serialNumber",
    "assetTag",
    "purchaseDate",
    "purchasePrice",
    "assignmentAction",
    "assignedToWorkspaceId",
    "assignedToUserId",
] as const;

/** Header names exposed so the idle-step copy stays in lock-step with the parser. */
export const ASSET_CSV_HEADERS = ALL_HEADERS;

// ISO calendar date the backend's `DateOnly` parser will accept directly.
// The bulk importer requires the canonical form so we don't silently
// coerce ambiguous regional variants ("3/4/24" — March or April?).
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// Canonical type set, lowercased, so the CSV check accepts any case
// the operator typed. The stored payload still uses the canonical
// PascalCase form via `canonicalAssetType` below.
const TYPE_VALUES_LOWER = new Set<string>(assetTypes.map((t) => t.toLowerCase()));
const ACTION_VALUES_LOWER = new Set<string>(assignmentActions.map((a) => a.toLowerCase()));

/**
 * Asset CSV parser. Sibling to `parseWorkspacesCsv` — same
 * RFC 4180-lite handling, same header policy, same per-row id
 * generation strategy. The two important differences:
 *
 *   1. Asset uniqueness is enforced on `serialNumber` and (when
 *      non-empty) `assetTag` rather than `name`. We surface within-file
 *      duplicates as per-row errors at parse time so the operator can
 *      fix the file before pressing Import and gets a clear "row N"
 *      pointer to the conflict.
 *
 *   2. `assignmentAction` (Preserve / Assign / Release) is required.
 *      Assign uses `assignedToWorkspaceId` (workspace id, name, or
 *      number) or `assignedToUserId` (user id, full name, or email).
 *      Preserve and Release must leave those columns blank so a re-upload
 *      cannot silently unassign or move an existing asset.
 */
export function parseAssetsCsv(
    text: string,
    offices: readonly SageOfficeView[],
    workspaces: readonly WorkspaceView[] = [],
): ParseAssetsCsvResult {
    const officeIndex = new Map<string, SageOfficeView>();
    for (const o of offices) officeIndex.set(o.id, o);

    const workspaceIndex = new Map<string, WorkspaceView>();
    for (const w of workspaces) workspaceIndex.set(w.id, w);

    const lines = splitCsvLines(text);
    if (lines.length === 0) {
        return { fatalError: "The file appears to be empty.", rows: [], hasErrors: true };
    }

    const headers = parseCsvLine(lines[0] ?? "").map((h) => h.trim());
    const headerError = validateHeader(headers);
    if (headerError !== null) {
        return { fatalError: headerError, rows: [], hasErrors: true };
    }

    const colIndex = new Map<string, number>();
    headers.forEach((h, i) => colIndex.set(h, i));

    const activeOfficeHint = buildAvailableOfficesHint(offices);

    // First pass: scan rows for within-file uniqueness on serialNumber
    // and (non-empty) assetTag. We need to record which row owns each
    // key so the *later* occurrence is the one tagged as a duplicate —
    // the first instance passes through and is sent normally.
    interface RawRow {
        readonly rowIndex: number;
        readonly assetId: string;
        readonly sageOfficeId: string;
        readonly type: string;
        readonly make: string;
        readonly model: string;
        readonly serialNumber: string;
        readonly assetTag: string;
        readonly purchaseDate: string;
        readonly purchasePrice: string;
        readonly assignmentAction: string;
        readonly assignedToWorkspaceId: string;
        readonly assignedToUserId: string;
    }
    const raws: RawRow[] = [];
    for (let i = 1; i < lines.length; i++) {
        const raw = lines[i];
        if (raw === undefined || raw.trim() === "") continue;
        const cells = parseCsvLine(raw);
        raws.push({
            rowIndex: i + 1,
            assetId: (cells[colIndex.get("assetId") ?? -1] ?? "").trim(),
            sageOfficeId: (cells[colIndex.get("sageOfficeId") ?? -1] ?? "").trim(),
            type: (cells[colIndex.get("type") ?? -1] ?? "").trim(),
            make: (cells[colIndex.get("make") ?? -1] ?? "").trim(),
            model: (cells[colIndex.get("model") ?? -1] ?? "").trim(),
            serialNumber: (cells[colIndex.get("serialNumber") ?? -1] ?? "").trim(),
            assetTag: (cells[colIndex.get("assetTag") ?? -1] ?? "").trim(),
            purchaseDate: (cells[colIndex.get("purchaseDate") ?? -1] ?? "").trim(),
            purchasePrice: (cells[colIndex.get("purchasePrice") ?? -1] ?? "").trim(),
            assignmentAction: (cells[colIndex.get("assignmentAction") ?? -1] ?? "").trim(),
            assignedToWorkspaceId: (
                cells[colIndex.get("assignedToWorkspaceId") ?? -1] ?? ""
            ).trim(),
            assignedToUserId: (cells[colIndex.get("assignedToUserId") ?? -1] ?? "").trim(),
        });
    }

    // Build first-seen maps using lowercased keys so the within-file
    // check matches the server's case-insensitive uniqueness guard.
    const serialFirstSeen = new Map<string, number>();
    const tagFirstSeen = new Map<string, number>();
    const assetIdFirstSeen = new Map<string, number>();
    for (const r of raws) {
        if (r.serialNumber !== "" && !serialFirstSeen.has(r.serialNumber.toLowerCase())) {
            serialFirstSeen.set(r.serialNumber.toLowerCase(), r.rowIndex);
        }
        if (r.assetTag !== "" && !tagFirstSeen.has(r.assetTag.toLowerCase())) {
            tagFirstSeen.set(r.assetTag.toLowerCase(), r.rowIndex);
        }
        if (r.assetId !== "" && !assetIdFirstSeen.has(r.assetId.toLowerCase())) {
            assetIdFirstSeen.set(r.assetId.toLowerCase(), r.rowIndex);
        }
    }

    const rows: ParsedAssetRow[] = [];
    let anyError = false;

    for (const r of raws) {
        const assetTag = r.assetTag === "" ? null : r.assetTag;
        let assignedToWorkspaceId =
            r.assignedToWorkspaceId === "" ? null : r.assignedToWorkspaceId;
        const assignedToUserId = r.assignedToUserId === "" ? null : r.assignedToUserId;

        const errors: string[] = [];
        if (r.sageOfficeId === "") errors.push("sageOfficeId is required.");
        if (r.make === "") errors.push("make is required.");
        if (r.model === "") errors.push("model is required.");
        if (r.serialNumber === "") errors.push("serialNumber is required.");
        if (r.type === "") {
            errors.push("type is required.");
        } else if (!TYPE_VALUES_LOWER.has(r.type.toLowerCase())) {
            errors.push(`type must be one of: ${assetTypes.join(", ")}.`);
        }

        const assignmentAction =
            r.assignmentAction === "" ? "" : canonicalAssignmentAction(r.assignmentAction);
        if (r.assignmentAction === "") {
            errors.push("assignmentAction is required (Preserve, Assign, or Release).");
        } else if (!ACTION_VALUES_LOWER.has(r.assignmentAction.toLowerCase())) {
            errors.push(`assignmentAction must be one of: ${assignmentActions.join(", ")}.`);
        }

        let purchaseDate: string | null = null;
        if (r.purchaseDate !== "") {
            if (!ISO_DATE_PATTERN.test(r.purchaseDate)) {
                errors.push(`purchaseDate '${r.purchaseDate}' must be in YYYY-MM-DD format.`);
            } else {
                purchaseDate = r.purchaseDate;
            }
        }

        let purchasePrice: number | null = null;
        if (r.purchasePrice !== "") {
            const parsed = Number(r.purchasePrice);
            if (Number.isNaN(parsed)) {
                errors.push(`purchasePrice '${r.purchasePrice}' must be a number.`);
            } else if (parsed < 0) {
                errors.push(`purchasePrice '${r.purchasePrice}' cannot be negative.`);
            } else {
                purchasePrice = parsed;
            }
        }

        const officeMatch = r.sageOfficeId !== "" ? officeIndex.get(r.sageOfficeId) : undefined;
        if (r.sageOfficeId !== "" && officeMatch === undefined) {
            errors.push(
                activeOfficeHint !== null
                    ? `sageOfficeId '${r.sageOfficeId}' is not a known sage office. ${activeOfficeHint}`
                    : `sageOfficeId '${r.sageOfficeId}' is not a known sage office.`,
            );
        } else if (officeMatch !== undefined && officeMatch.isDeleted) {
            errors.push(`sageOfficeId '${r.sageOfficeId}' refers to a deleted sage office.`);
        }

        const actionLower = assignmentAction.toLowerCase();
        if (actionLower === "assign") {
            if (assignedToWorkspaceId !== null && assignedToUserId !== null) {
                errors.push(
                    "Fill assignedToWorkspaceId or assignedToUserId, not both. An asset can have only one open assignment.",
                );
            } else if (assignedToWorkspaceId === null && assignedToUserId === null) {
                errors.push(
                    "assignmentAction Assign requires assignedToWorkspaceId or assignedToUserId.",
                );
            }
        } else if (
            (actionLower === "preserve" || actionLower === "release") &&
            (assignedToWorkspaceId !== null || assignedToUserId !== null)
        ) {
            errors.push(
                `assignmentAction ${assignmentAction} leaves assignment unchanged or unassigned; leave assignedToWorkspaceId and assignedToUserId blank, or use Assign.`,
            );
        }

        let workspaceMatch =
            assignedToWorkspaceId !== null ? workspaceIndex.get(assignedToWorkspaceId) : undefined;
        if (assignedToWorkspaceId !== null && workspaceMatch === undefined) {
            const resolved = resolveWorkspaceToken(assignedToWorkspaceId, workspaces);
            if (resolved.error !== null) errors.push(resolved.error);
            assignedToWorkspaceId = resolved.workspace?.id ?? assignedToWorkspaceId;
            workspaceMatch = resolved.workspace;
        }
        if (workspaceMatch !== undefined && workspaceMatch.isDeleted) {
            errors.push(
                `assignedToWorkspaceId '${assignedToWorkspaceId}' refers to a deleted workspace.`,
            );
        }

        if (r.serialNumber !== "") {
            const owner = serialFirstSeen.get(r.serialNumber.toLowerCase());
            if (owner !== undefined && owner !== r.rowIndex) {
                errors.push(
                    `serialNumber '${r.serialNumber}' already appears on row ${owner} of this file.`,
                );
            }
        }
        if (assetTag !== null) {
            const owner = tagFirstSeen.get(assetTag.toLowerCase());
            if (owner !== undefined && owner !== r.rowIndex) {
                errors.push(`assetTag '${assetTag}' already appears on row ${owner} of this file.`);
            }
        }
        if (r.assetId !== "") {
            const owner = assetIdFirstSeen.get(r.assetId.toLowerCase());
            if (owner !== undefined && owner !== r.rowIndex) {
                errors.push(`assetId '${r.assetId}' already appears on row ${owner} of this file.`);
            }
        }

        if (errors.length > 0) anyError = true;

        const isAssign = actionLower === "assign";

        rows.push({
            rowIndex: r.rowIndex,
            payload: {
                sourceRowIndex: r.rowIndex,
                id: r.assetId !== "" ? r.assetId : crypto.randomUUID(),
                sageOfficeId: r.sageOfficeId,
                type: r.type === "" ? r.type : canonicalAssetType(r.type),
                make: r.make,
                model: r.model,
                serialNumber: r.serialNumber,
                assetTag,
                purchaseDate,
                purchasePrice,
                assignmentAction,
                assignedToWorkspaceId,
                assignedToUserId,
                assignmentId: isAssign ? crypto.randomUUID() : null,
            },
            errors,
            sageOfficeName: officeMatch?.name ?? null,
            workspaceName: workspaceMatch?.name ?? null,
        });
    }

    if (rows.length === 0) {
        return {
            fatalError: "No data rows found below the header.",
            rows: [],
            hasErrors: true,
        };
    }

    return { fatalError: null, rows, hasErrors: anyError };
}

/**
 * Accept a workspace id, or a case-insensitive name, friendly name, or
 * floorplan element id. Unknown tokens are left as-is so a catalog the
 * parser has not loaded can still be posted. Two workspaces with the
 * same name is an error — the id is the unambiguous form.
 */
function resolveWorkspaceToken(
    token: string,
    workspaces: readonly WorkspaceView[],
): {
    readonly workspace: WorkspaceView | undefined;
    readonly error: string | null;
} {
    const query = token.trim().toLowerCase();
    const named = workspaces.filter((workspace) => {
        const name = workspace.name.trim().toLowerCase();
        const friendly = (workspace.friendlyName ?? "").trim().toLowerCase();
        const element = (workspace.floorplanElementId ?? "").trim().toLowerCase();
        return (
            name === query ||
            (friendly !== "" && friendly === query) ||
            (element !== "" && element === query)
        );
    });
    const active = named.filter((workspace) => !workspace.isDeleted);
    const pool = active.length > 0 ? active : named;
    const match = pool[0];
    if (pool.length === 1 && match !== undefined) {
        return { workspace: match, error: null };
    }
    if (pool.length > 1) {
        return {
            workspace: undefined,
            error: `assignedToWorkspaceId '${token}' matches more than one workspace. Use the workspace id.`,
        };
    }
    return { workspace: undefined, error: null };
}

function validateHeader(headers: readonly string[]): string | null {
    const missing = REQUIRED_HEADERS.filter((h) => !headers.includes(h));
    if (missing.length > 0) {
        return `Header is missing required column(s): ${missing.join(", ")}. Expected: ${ALL_HEADERS.join(", ")}.`;
    }
    const unknown = headers.filter(
        (h) => h !== "" && !ALL_HEADERS.includes(h as (typeof ALL_HEADERS)[number]),
    );
    if (unknown.length > 0) {
        return `Header has unrecognized column(s): ${unknown.join(", ")}. Expected: ${ALL_HEADERS.join(", ")}.`;
    }
    return null;
}

function splitCsvLines(text: string): string[] {
    const normalized = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    const lines = normalized.split("\n");
    if (lines.length > 0 && lines[lines.length - 1] === "") lines.pop();
    return lines;
}

function parseCsvLine(line: string): string[] {
    const cells: string[] = [];
    let current = "";
    let inQuotes = false;
    let i = 0;

    while (i < line.length) {
        const ch = line[i];
        if (inQuotes) {
            if (ch === '"') {
                if (line[i + 1] === '"') {
                    current += '"';
                    i += 2;
                    continue;
                }
                inQuotes = false;
                i++;
                continue;
            }
            current += ch;
            i++;
            continue;
        }
        if (ch === ",") {
            cells.push(current);
            current = "";
            i++;
            continue;
        }
        if (ch === '"' && current === "") {
            inQuotes = true;
            i++;
            continue;
        }
        current += ch;
        i++;
    }
    cells.push(current);
    return cells;
}

/**
 * Build the CSV template the user can download to seed their file.
 * Includes three example rows covering assignment actions:
 *   - Assign to a workspace (tag + date + price)
 *   - Assign to a user (partial acquisition data)
 *   - Preserve (create unassigned / leave existing assignment)
 *
 * `assetId` is left blank so first-time imports resolve by serial.
 * When at least one active office / workspace is loaded, example rows
 * use real ids from the current tenant so the template is immediately
 * importable. Falls back to clearly-fake placeholders otherwise.
 */
export function buildAssetCsvTemplate(
    offices: readonly SageOfficeView[] = [],
    workspaces: readonly WorkspaceView[] = [],
): string {
    const exampleOffice = offices.find((o) => !o.isDeleted) ?? null;
    const exampleId = exampleOffice?.id ?? "<paste-office-id-here>";
    const exampleWorkspace = workspaces.find((w) => !w.isDeleted) ?? null;
    const exampleWorkspaceToken = exampleWorkspace?.name ?? "<workspace-name-or-id>";
    const header = ALL_HEADERS.join(",");
    const examples = [
        `,${csvEscape(exampleId)},Laptop,Dell,Latitude 7440,SN-LT-0001,IT-00123,2024-03-15,1299.99,Assign,${csvEscape(exampleWorkspaceToken)},`,
        `,${csvEscape(exampleId)},Monitor,LG,27UP850,SN-MN-0042,,2023-11-02,,Assign,,ada@example.com`,
        `,${csvEscape(exampleId)},Headset,Jabra,Evolve2 65,SN-HS-0017,IT-00417,,189.00,Preserve,,`,
    ];
    return [header, ...examples].join("\n") + "\n";
}

/**
 * Render up to a handful of office name+id pairs to embed inside an
 * unknown-id error message. Bounded so a tenant with hundreds of
 * offices doesn't produce a wall-of-text per failed row.
 */
function buildAvailableOfficesHint(offices: readonly SageOfficeView[]): string | null {
    const active = offices.filter((o) => !o.isDeleted);
    if (active.length === 0) return null;
    const SAMPLE = 3;
    const sample = active
        .slice(0, SAMPLE)
        .map((o) => `'${o.name}' (${o.id})`)
        .join(", ");
    if (active.length <= SAMPLE) {
        return `Available: ${sample}.`;
    }
    return `Available (${SAMPLE} of ${active.length}): ${sample}.`;
}

/**
 * Build a CSV from the failed rows of a previous import so the
 * operator can fix and re-run only the rejects. Includes the original
 * column shape plus the server's per-row error message in a trailing
 * `error` column the parser will reject as unknown — that's
 * intentional, the user is expected to delete the column before
 * re-uploading.
 */
export function buildFailedRowsCsv(
    failed: readonly { row: ParsedAssetRow; errorMessage: string }[],
): string {
    const header = [...ALL_HEADERS, "error"].join(",");
    const lines = failed.map(({ row, errorMessage }) =>
        [
            csvEscape(row.payload.id),
            csvEscape(row.payload.sageOfficeId),
            csvEscape(row.payload.type),
            csvEscape(row.payload.make),
            csvEscape(row.payload.model),
            csvEscape(row.payload.serialNumber),
            csvEscape(row.payload.assetTag ?? ""),
            csvEscape(row.payload.purchaseDate ?? ""),
            csvEscape(row.payload.purchasePrice !== null ? String(row.payload.purchasePrice) : ""),
            csvEscape(row.payload.assignmentAction),
            csvEscape(row.payload.assignedToWorkspaceId ?? ""),
            csvEscape(row.payload.assignedToUserId ?? ""),
            csvEscape(errorMessage),
        ].join(","),
    );
    return [header, ...lines].join("\n") + "\n";
}

/**
 * Join API per-row results back to the parsed preview table.
 *
 * Both sides use spreadsheet line numbers (header = 1, first data = 2).
 * Matching on `rowIndex` is required — do not use payload `id`, because a
 * re-upload can resolve to a different existing asset id via serial.
 */
export function correlateFailedImportRows(
    rows: readonly ParsedAssetRow[],
    results: readonly {
        readonly rowIndex: number;
        readonly outcome: string;
        readonly errorMessage?: string | null | undefined;
    }[],
): readonly { row: ParsedAssetRow; errorMessage: string }[] {
    const rowsByIndex = new Map<number, ParsedAssetRow>();
    for (const r of rows) rowsByIndex.set(r.rowIndex, r);
    return results
        .filter((r) => r.outcome === "failed")
        .flatMap((r) => {
            const row = rowsByIndex.get(r.rowIndex);
            if (row === undefined) return [];
            return [{ row, errorMessage: r.errorMessage ?? "Unknown error." }];
        });
}
